import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/layout/TopNavigation';
import ScrollToTop from './components/common/ScrollToTop';
import AdminLogin from './features/admin/AdminLogin';
import BookingActionHandler from './features/admin/BookingActionHandler';
import GraphicDesigning from './features/services/GraphicDesigning';
import DigitalMarketing from './features/services/DigitalMarketing';
import MobileAppDevelopment from './features/services/MobileAppDevelopment';
import AdminProtectedRoute from './components/common/AdminProtectedRoute';
import WebDevelopment from './features/services/WebDevelopment';
import SaaSSoftware from './features/services/SaaSSoftware';
import DataIntelligence from './features/services/DataIntelligence';
const AskLoki = lazy(() => import('./features/loki/AskLoki'));

// Lazy load components
const HomePage = lazy(() => import('./pages/HomePage'));
const OurServices = lazy(() => import('./pages/Services'));
const Blogs = lazy(() => import('./pages/Blogs'));
const NewsSection = lazy(() => import('./components/common/NewsSection'));

const TeamPage = lazy(() => import('./pages/TeamPage'));
const PartnersSection = lazy(() => import('./components/common/PartnersSection'));
const TestimonialsSection = lazy(() => import('./components/common/TestimonialsSection'));
const Footer = lazy(() => import('./components/layout/Footer'));
const Maintainance = lazy(() => import('./pages/Maintenance'));
const ServiceBookingDashboard = lazy(() => import('./features/bookings/Dashboard'));
const AdminDashboard = lazy(() => import('./features/admin/AdminDashboard'));

const BlogManagement = lazy(() => import('./features/admin/BlogManagement'));
const ServiceManagement = lazy(() => import('./features/admin/ServiceManagement'));
const PortfolioSections = lazy(() => import('./pages/Services'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const MyAccount = lazy(() => import('./pages/MyAccount'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-white dark:bg-[#021E23] transition-colors duration-300">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
  </div>
);

// Maintenance placeholder routes
const maintainanceRoutes = [

  '/see-more',
  '/discover',
  '/events',
  '/news',
  '/demo'
];


const App = () => {
  const hostname = window.location.hostname;
  const isAdminSubdomain = hostname.startsWith('admin.');

  return (
    <ThemeProvider>
      <Router>
        <ScrollToTop />
        <div className="font-sora min-h-screen bg-white dark:bg-[#021E23] transition-colors duration-300">
          {!isAdminSubdomain && <Navbar />}

          {!isAdminSubdomain && (
            <Suspense fallback={null}>
              <AskLoki />
            </Suspense>
          )}

          <main className={!isAdminSubdomain ? "pt-14 sm:pt-[72px]" : ""}>
            <Suspense fallback={<LoadingSpinner />}>
              {isAdminSubdomain ? (
                <Routes>
                  {/* Admin Subdomain Routes - Mapping root to dashboard/login */}
                  <Route path="/" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
                  <Route path="/login" element={<AdminLogin />} />
                  <Route path="/blogs" element={<AdminProtectedRoute><BlogManagement /></AdminProtectedRoute>} />
                  <Route path="/bookings/:id/:action" element={<AdminProtectedRoute><BookingActionHandler /></AdminProtectedRoute>} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              ) : (
                <Routes>
                  {/* Public Landing */}
                  <Route
                    path="/"
                    element={
                      <>
                        <HomePage />
                        <OurServices />
                        <NewsSection />
                        <PartnersSection />
                        <TestimonialsSection />
                      </>
                    }
                  />

                  {/* Public Static Pages */}
                  <Route path="/services" element={<OurServices />} />
                  <Route path="/portfolio" element={<PortfolioSections />} />
                  <Route path="/team" element={<TeamPage />} />
                  <Route path="/about" element={<AboutUs />} />
                  <Route path="/graphic-designing" element={<GraphicDesigning />} />
                  <Route path="/contact" element={<ContactUs />} />
                  <Route path="/digital-marketing" element={<DigitalMarketing />} />
                  <Route path="/blogs" element={<Blogs />} />
                  <Route path="/blogs/:id" element={<BlogDetail />} />
                  <Route path="/mobile-app-development" element={<MobileAppDevelopment />} />
                  <Route path="/web-development" element={<WebDevelopment />} />
                  <Route path="/saas-software" element={<SaaSSoftware />} />
                  <Route path="/data-intelligence" element={<DataIntelligence />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/brand-assets" element={<NotFoundPage />} />

                  {/* Authentication */}
                  <Route path="/login" element={<NotFoundPage />} />
                  <Route path="/signup" element={<NotFoundPage />} />
                  <Route path="/forgot-password" element={<NotFoundPage />} />
                  <Route path="/reset-password" element={<NotFoundPage />} />
                  <Route path="/auth/verify-email" element={<NotFoundPage />} />

                  {/* User Account & Dashboard */}
                  <Route path="/my-account" element={<MyAccount />} />
                  <Route path="/dashboard" element={<ServiceBookingDashboard />} />
                  <Route path="/bookings/:id" element={<ServiceBookingDashboard />} />

                  {/* Admin Panel (also accessible via path for compatibility) */}
                  <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/blogs" element={<AdminProtectedRoute><BlogManagement /></AdminProtectedRoute>} />
                  <Route path="/admin/services" element={<AdminProtectedRoute><ServiceManagement /></AdminProtectedRoute>} />
                  <Route path="/admin/bookings/:id/:action" element={<AdminProtectedRoute><BookingActionHandler /></AdminProtectedRoute>} />

                  {/* Maintenance Routes */}
                  {maintainanceRoutes.map(path => (
                    <Route key={path} path={path} element={<Maintainance />} />
                  ))}

                  {/* Catch-All */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              )}
            </Suspense>
          </main>
          {!isAdminSubdomain && <Footer />}
        </div>
      </Router>
    </ThemeProvider>
  );
};

export default App;
